import { SmallAddIcon } from "@chakra-ui/icons";
import { Button, HStack, Link, MenuItem } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ContactsUpsertMutationBody,
  getContactsTableDataRetrieveQueryKey,
  useContactsTableDataRetrieveSuspense,
  useContactsUpsert,
} from "gen/endpoints/contact/contact";
import {
  getContactsListQueryKey,
  useContactsListSuspense,
} from "gen/endpoints/contact-bulk/contact-bulk";
import { ContactEnrichedCreateData } from "gen/model/contactEnrichedCreateData";
import { ContactEnrichedReadDataList } from "gen/model/contactEnrichedReadDataList";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import { FormDrawer } from "shared/components/form/FormDrawer";
import { FormFieldPrefix } from "shared/components/form/FormFieldPrefix";
import { FormFieldset } from "shared/components/form/FormFieldset";
import { FormHiddenInput } from "shared/components/form/FormHiddenInput";
import { FormCheckbox } from "shared/components/input/checkbox/Checkbox";
import { FormJurisdictionSelect } from "shared/components/input/select/JurisdictionSelect";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActionsMenu } from "shared/components/table/box/TableBoxActionsMenu";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxCollapse } from "shared/components/table/box/TableBoxCollapse";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { MoreHorizontalIcon } from "shared/icons/MoreHorizontalIcon";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

export const InvestorEntityContactInfoBlock: React.FC<{
  investorEntityId: string;
}> = ({ investorEntityId }) => {
  const { t } = useTranslation(["investorEntity", "entity", "common"]);

  const { data: tableData } =
    useContactsTableDataRetrieveSuspense(investorEntityId);
  const { data: listData } = useContactsListSuspense({
    entity_id: investorEntityId,
  });

  const {
    value: isDrawerOpen,
    setTrue: openDrawer,
    setFalse: closeDrawer,
  } = useBoolean();

  return (
    <>
      <TableBox
        isDefaultExpanded={tableData.rows.length > 0}
        boxProps={{ sx: { mb: "16px" } }}
      >
        <TableBoxHeader>
          <TableBoxTitle textStyle="bodyBold">
            {t("investorEntity:contactInfo.title")}
          </TableBoxTitle>
          <DataTableBoxActions
            endChildren={
              <Restricted permission={PermissionTypeEnum.config}>
                <TableBoxActionsMenu IconComponent={MoreHorizontalIcon}>
                  <MenuItem onClick={openDrawer}>{t("common:edit")}</MenuItem>
                </TableBoxActionsMenu>
              </Restricted>
            }
          />
        </TableBoxHeader>
        <TableBoxCollapse>
          <TableBoxBody>
            <DataTable
              data={tableData}
              emptyStateProps={{
                title: t("investorEntity:contactInfo.empty.title"),
                children: (
                  <Restricted permission={PermissionTypeEnum.config}>
                    <Button colorScheme="secondary" onClick={openDrawer}>
                      {t("common:configure")}
                    </Button>
                  </Restricted>
                ),
              }}
            />
          </TableBoxBody>
        </TableBoxCollapse>
      </TableBox>
      {isDrawerOpen && (
        <ContactInfoEditDrawer
          investorEntityId={investorEntityId}
          onCloseComplete={closeDrawer}
          data={listData}
        />
      )}
    </>
  );
};

const ContactInfoEditDrawer: React.FC<{
  investorEntityId: string;
  onCloseComplete: () => void;
  data: ContactEnrichedReadDataList;
}> = ({ investorEntityId, onCloseComplete, data }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["investorEntity", "entity", "common"]);

  const { mutateAsync } = useContactsUpsert();

  const form = useForm<ContactsUpsertMutationBody>({
    defaultValues: {
      upsert_data_list: data.length
        ? data
        : [makeEmptyContactEnrichedCreateData(investorEntityId)],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "upsert_data_list",
  });

  const onSubmit = async (data: ContactsUpsertMutationBody) => {
    await mutateAsync({ investorEntityId, data });
  };

  const onSuccess = async () => {
    await invalidateQueryKeys(
      queryClient,
      getContactsTableDataRetrieveQueryKey(investorEntityId),
      getContactsListQueryKey({ entity_id: investorEntityId }),
    );
  };

  return (
    <FormDrawer
      title={t("investorEntity:contactInfo.edit.title")}
      formProps={{
        form,
        onSubmit,
        onSuccess,
      }}
      onCloseComplete={onCloseComplete}
    >
      {fields.map((_, index) => (
        <ContactInfoFormInputs
          key={index}
          investorEntityId={investorEntityId}
          index={index}
          remove={() => remove(index)}
        />
      ))}
      <Button
        colorScheme="secondary"
        size="sm"
        iconSpacing="2px"
        leftIcon={<SmallAddIcon />}
        onClick={() =>
          append(makeEmptyContactEnrichedCreateData(investorEntityId))
        }
      >
        {t("investorEntity:contactInfo.edit.add")}
      </Button>
    </FormDrawer>
  );
};

const ContactInfoFormInputs: React.FC<{
  index: number;
  investorEntityId: string;
  remove: () => void;
}> = ({ index, investorEntityId, remove }) => {
  const { t } = useTranslation(["investorEntity", "entity", "common"]);
  return (
    <FormFieldPrefix prefix={`upsert_data_list.${index}`}>
      <FormFieldset border="1px dashed" borderColor="gray.400">
        <FormHiddenInput name="entity_id" value={investorEntityId} />
        <FormTextInput
          name="name"
          title={t("investorEntity:contactInfo.edit.name")}
        />
        <FormTextInput
          name="email"
          title={t("investorEntity:contactInfo.edit.email")}
        />
        <FormTextInput
          name="address_data.street_address_one"
          title={t("investorEntity:contactInfo.edit.streetAddressOne")}
        />
        <FormTextInput
          name="address_data.street_address_two"
          title={t("investorEntity:contactInfo.edit.streetAddressTwo")}
        />
        <FormTextInput
          name="address_data.city"
          title={t("investorEntity:contactInfo.edit.city")}
        />
        <FormJurisdictionSelect
          countryFieldName="address_data.country"
          countryFieldTitle={t("investorEntity:contactInfo.edit.country")}
          stateFieldName="address_data.state"
          stateFieldTitle={t("investorEntity:contactInfo.edit.state")}
        />
        <FormTextInput
          name="address_data.postal_code"
          title={t("investorEntity:contactInfo.edit.postalCode")}
        />
        <FormCheckbox name="is_primary">
          {t("investorEntity:contactInfo.edit.isPrimary")}
        </FormCheckbox>
        <HStack justifyContent="flex-end">
          <Link size="sm" colorScheme="gray" onClick={() => remove()}>
            {t("investorEntity:contactInfo.edit.remove")}
          </Link>
        </HStack>
      </FormFieldset>
    </FormFieldPrefix>
  );
};

const makeEmptyContactEnrichedCreateData = (
  entityId: string,
): ContactEnrichedCreateData => ({
  entity_id: entityId,
  name: "",
  address_data: {
    name: "",
    street_address_one: "",
    street_address_two: "",
    city: "",
    state: "" as never,
    country: "" as never,
    postal_code: "",
  },
});
